import { CommonModule, Location } from '@angular/common';
import { ChangeDetectorRef, Component, ViewChild, NgZone } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { Router, ActivatedRoute, RouterLink } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { CiaoSharedModule } from '~app/components/shared/shared.module';
import { TripAttributes } from '~app/models';
import { CheckpointAttributes } from '~app/models/checkpoint';
import { v4 as uuidv4 } from 'uuid';

import { TripService } from '~app/services';
import { DatetimeService } from '~app/services/datetime.service';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);
import { OkayModalSectionComponent } from './okay-modal-section/okay-modal-section.component';
import { TripLogSectionComponent } from './trip-log-section/trip-log-section.component';
import { OkayModalComponent } from '~app/components/shared/okay-modal/okay-modal.component';
import {
  catchError,
  delay,
  map,
  switchMap,
  switchMapTo,
  take,
  tap,
} from 'rxjs/operators';
import { BehaviorSubject, Subscription } from 'rxjs';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TripPageDropdownOptionsService } from './trip-page-dropdown-options.service';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatSelectModule } from '@angular/material/select';

import { ColDef, RowSelectionOptions } from 'ag-grid-community'; // Column Definition Type Interface
import { TripMapComponent } from '../trip-summary/trip-map/trip-map.component';
import { MaterialModule } from '~app/components/shared/material.module';
import { FleetVehicleGridComponent } from './fleet-vehicle-grid/fleet-vehicle-grid.component';
import { LocationGridComponent } from './location-grid/location-grid.component';
import { CheckpointGridComponent } from './checkpoint-grid/checkpoint-grid.component';
import { ScheduledCheckpointModalComponent } from './scheduled-checkpoint-modal/scheduled-checkpoint-modal.component';
import { CrewMemberGridComponent } from './crew-member-grid/crew-member-grid.component';
import { FileUploadComponent } from '../trip-modal-v2/attachments-form/file-upload/file-upload.component';
import { CiaoFormsModule } from '~app/components/shared/forms/forms.module';
import { StatusBadgeComponent } from './status-badge-component';
import { CiaoModalComponent } from '~app/components/shared/ciao-modal/ciao-modal.component';
import { guestCrewHaveRequiredPoc } from '~app/utilities';

@Component({
  selector: 'ciao-trip-page-v3',
  imports: [
    CommonModule,
    RouterLink,
    MatIconModule,
    MaterialModule,
    ReactiveFormsModule,
    CiaoSharedModule,
    MatSelectModule,
    MatAutocompleteModule,
    TripMapComponent,
    OkayModalSectionComponent,
    TripLogSectionComponent,
    LocationGridComponent,
    FleetVehicleGridComponent,
    LocationGridComponent,
    CheckpointGridComponent,
    ScheduledCheckpointModalComponent,
    CrewMemberGridComponent,
    FileUploadComponent,
    FormsModule,
    CiaoFormsModule,
    StatusBadgeComponent,
    OkayModalComponent,
  ],
  providers: [Location],
  templateUrl: './trip-page-v3.component.html',
  styleUrl: './trip-page-v3.component.less',
})
export class TripPageV3Component {
  readonly formGroup = new FormGroup({
    userGroupId: new FormControl(''),
    startDate: new FormControl('', [
      Validators.required,
      this.startDateValidator.bind(this),
    ]),
    endDate: new FormControl('', [
      Validators.required,
      this.endDateValidator.bind(this),
      this.endDateInPastValidator.bind(this),
    ]),
    alertSchedule: new FormControl({ value: 'none', label: 'None (Default)' }),
  });

  originalDateValues: any = {};
  readonly startDateControl = this.formGroup.get('startDate');
  readonly endDateControl = this.formGroup.get('endDate');
  constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    public readonly tripService: TripService,
    private readonly toastr: ToastrService,
    private readonly dropdownOptionService: TripPageDropdownOptionsService,
    private readonly cdr: ChangeDetectorRef,
    private readonly ngZone: NgZone,
    private readonly dateTimeService: DatetimeService
  ) {}

  @ViewChild('locationsGrid') locationsGrid: LocationGridComponent;
  @ViewChild('tripMap') tripMap: TripMapComponent;
  @ViewChild('crewmemberGrid') crewMemberGrid: CrewMemberGridComponent;
  @ViewChild('checkpointsGrid') checkpointsGrid: CheckpointGridComponent;
  @ViewChild('fleetGrid') fleetGrid: FleetVehicleGridComponent;
  @ViewChild('checkPointModal')
  checkPointModal: ScheduledCheckpointModalComponent;
  @ViewChild('deleteTripConfirmModal')
  deleteTripConfirmModal: CiaoModalComponent;

  gridErrorStates = {
    locationsGrid: null,
    crewMemberGrid: null,
    checkpointsGrid: null,
    fleetGrid: null,
  };
  toastrAllErrorToastId: number;

  subscriptions = new Subscription();

  readonly refresh$ = new BehaviorSubject<void>(null);
  readonly tripId$ = new BehaviorSubject<string>(null);
  readonly trip$ = new BehaviorSubject<TripAttributes>(null);
  tripIsLoading;
  tripId;
  selectedSchedule: string = 'none';
  /** When true, the map fills the top box and the Location Details table drops
   * to full width below the top row. */
  mapExpanded: boolean = false;
  savedSchedule: string = 'none'; // Track the last saved schedule
  hasScheduleChanged: boolean = false;
  hasInitializedSchedule: boolean = false; // Track if we've already detected schedule from checkpoints
  private destroyed = false;

  readonly userGroupOptions$ = this.dropdownOptionService.userGroupOptions$;
  readonly stateOptions$ = this.dropdownOptionService.stateOptions$;

  readonly alertScheduleOptions$ = new BehaviorSubject([
    { value: 'none', label: 'None (Default)' },
    { value: 'twice-daily', label: 'Twice Daily (9:00AM | 9:00PM)' },
    {
      value: 'three-times',
      label: 'Three Times / Day (9:00AM | 1:00PM | 9:00PM)',
    },
    { value: 'custom', label: 'Custom' },
  ]);

  readonly tripFromTripServiceSub = this.refresh$
    .pipe(
      switchMapTo(this.route.params),
      map((params) => params.id),
      tap(this.tripId$),
      switchMap((tripId) => this.tripService.findById(tripId)),
      // the BehaviorSubject can act as a list of functions that fits
      // perfectly with the tap/subscribe parameter of next/error/complete
      // We are piping the result of the switchMap into the trip$ behavior subject.
      // Simpler/Cleaner replacement for <div *ngIf="trip$ | async as trip"/>
      tap(this.trip$),
      tap((trip) => {
        this.tripId = trip.id;
        // Patch only trip fields that exist on the form; alertSchedule is set from checkpoints below
        this.formGroup.patchValue({
          startDate: trip.startDate,
          endDate: trip.endDate,
        });

        this.originalDateValues = {
          startDate: this.startDateControl.value,
          endDate: this.endDateControl.value,
        };

        // Closed trips are read-only: lock the editable Details controls so the
        // inline inputs can't be changed (and won't auto-save). emitEvent:false
        // avoids triggering the valueChanges auto-save while toggling enabled
        // state.
        const editableControls = ['startDate', 'endDate', 'alertSchedule'];
        const closed = trip.tripStatus === 'Closed';
        for (const name of editableControls) {
          const control = this.formGroup.get(name);
          if (!control) continue;
          if (closed && control.enabled) {
            control.disable({ emitEvent: false });
          } else if (!closed && control.disabled) {
            control.enable({ emitEvent: false });
          }
        }

        // Set alert schedule dropdown from saved checkpoints so it shows after refresh.
        // Use setTimeout so the form/combobox is in the DOM and the value accessor has been registered.
        setTimeout(() => this.detectAndSetScheduleFromCheckpoints(trip), 0);
      }),
      delay(0),
      // Check for validity on all tables again.
      tap(() => {
        // Send Out Trip to everyone
        // (cause it's going to happen in a moment anyway)
        this.cdr.detectChanges();
        // Check Validity
        this.locationsGrid?.isTableValid();
        this.crewMemberGrid?.isTableValid();
        this.checkpointsGrid?.isTableValid();
        this.fleetGrid?.isTableValid();
      })
    )
    .subscribe();

  get trip() {
    if (this.destroyed) return null;
    return this.trip$.value;
  }

  get showCheckedIn() {
    return this.trip.crewMembersProcessed.every(
      (crewMember) => crewMember?.crewMemberStatus === 'CheckedIn'
    );
  }

  get dateTimeErrors() {
    return this.endDateControl?.errors || this.startDateControl?.errors;
  }

  get canCheckout() {
    return (
      !this._hasGridErrors &&
      this.trip &&
      this.trip.id &&
      this.trip.startDate &&
      this.trip.endDate &&
      this.trip.usergroup &&
      this.trip.locations.length > 0 &&
      this.trip.crewMembers.length > 0 &&
      guestCrewHaveRequiredPoc(this.trip.crewMembers)
    );
  }

  onFieldBlur(field: string, event) {
    const currentVal = this.formGroup.get(field)?.value;
    if (currentVal !== this.originalDateValues[field] && this.formGroup.valid) {
      this.saveTrip();
    }
    return;
  }

  /** Toggle the expanded map layout, then let Leaflet recompute its size once the
   * container has resized so tiles re-render at the new dimensions. */
  toggleMapExpanded() {
    this.mapExpanded = !this.mapExpanded;
    this.cdr.detectChanges();
    setTimeout(() => this.tripMap?.refreshSize(), 300);
  }

  /** Map "add location" click routes through the component to the location grid
   * rendered above the map. */
  onMapAddLocation(event) {
    this.locationsGrid?.onMapClick(event);
  }

  onCalendarClick() {
    if (this.canCheckout) {
      this.tripService.addToCalendar(this.trip);
    }
  }

  ngOnInit() {
    // Subscribe to alert schedule changes
    this.subscriptions.add(
      this.formGroup.get('alertSchedule').valueChanges.subscribe((value) => {
        // Extract the schedule value - handle both object and string formats
        let scheduleValue: string;
        if (typeof value === 'string') {
          scheduleValue = value;
        } else if (value && typeof value === 'object' && value.value) {
          scheduleValue = value.value;
        } else if (value && typeof value === 'object' && value.label) {
          // Fallback: try to find by label
          const option = this.alertScheduleOptions$.value.find(
            (opt) => opt.label === value.label
          );
          scheduleValue = option?.value || 'none';
        } else {
          scheduleValue = 'none';
        }

        // Update the selected schedule immediately
        const previousSchedule = this.selectedSchedule;
        this.selectedSchedule = scheduleValue;

        // Track if schedule has changed from saved value
        this.hasScheduleChanged = this.selectedSchedule !== this.savedSchedule;

        // Ensure change detection runs in Angular zone to render the grid first
        this.ngZone.run(() => {
          this.cdr.markForCheck();
          this.cdr.detectChanges();
        });

        // Auto-generate and save checkpoints when a preset schedule is selected
        // Use setTimeout to ensure grid is rendered in DOM first (longer delay for animation and DOM rendering)
        if (
          this.selectedSchedule === 'twice-daily' ||
          this.selectedSchedule === 'three-times'
        ) {
          const schedule = this.selectedSchedule as
            | 'twice-daily'
            | 'three-times';
          setTimeout(() => {
            // Wait for grid to be rendered in DOM before trying to access it
            this.ngZone.run(() => {
              // Automatically save the schedule when selected
              this.generateScheduledCheckpoints(schedule, () => {
                this.savedSchedule = this.selectedSchedule;
                this.hasScheduleChanged = false;
              });
            });
          }, 800); // Increased delay to allow DOM rendering
        } else if (this.selectedSchedule === 'custom') {
          // Clear preset schedules when switching to custom and update grid so only custom rows remain
          setTimeout(() => {
            this.ngZone.run(() => {
              this.clearScheduledCheckpoints((savedCheckpoints) => {
                // Update grid to show only the saved checkpoints (no preset Morning/Mid-Day/Evening)
                if (this.checkpointsGrid) {
                  const rows = savedCheckpoints ?? [];
                  this.checkpointsGrid.rowData = rows;
                  if (this.checkpointsGrid.gridApi) {
                    this.checkpointsGrid.gridApi.setGridOption(
                      'rowData',
                      rows
                    );
                  }
                  this.cdr.detectChanges();
                }
                this.savedSchedule = this.selectedSchedule;
                this.hasScheduleChanged = false;
                // After clearing auto-generated, save any manual checkpoints in the grid
                if (
                  this.checkpointsGrid &&
                  this.checkpointsGrid.gridRows?.length > 0
                ) {
                  setTimeout(() => {
                    this.checkpointsGrid.saveCheckpointData();
                  }, 100);
                }
              });
            });
          }, 800); // Increased delay to allow DOM rendering
        } else if (this.selectedSchedule === 'none') {
          // Clear all checkpoints when switching to none and auto-save
          setTimeout(() => {
            this.ngZone.run(() => {
              this.clearScheduledCheckpoints(() => {
                this.savedSchedule = this.selectedSchedule;
                this.hasScheduleChanged = false;
                // Update the grid to show empty state
                const updateGrid = () => {
                  if (this.checkpointsGrid && this.checkpointsGrid.gridApi) {
                    this.checkpointsGrid.rowData = [];
                    this.checkpointsGrid.gridApi.setGridOption('rowData', []);
                    this.cdr.detectChanges();
                  } else {
                    setTimeout(() => updateGrid(), 100);
                  }
                };
                updateGrid();
              });
            });
          }, 800); // Increased delay to allow DOM rendering
        }
      })
    );
  }

  generateScheduledCheckpoints(
    scheduleType: 'twice-daily' | 'three-times',
    onSuccess?: () => void
  ) {
    const trip = this.trip;
    if (!trip || !trip.startDate || !trip.endDate) {
      this.toastr.warning(
        'Trip start and end dates are required to generate schedules'
      );
      return;
    }

    // Get current checkpoints from the grid (includes any manual edits and respects deletions)
    const existingCheckpoints = this.getAllCheckpointsFromGrid();
    const autoGeneratedNames = [
      'Morning Alert',
      'Mid-Day Alert',
      'Evening Alert',
    ];

    // Separate manual checkpoints from scheduled ones
    const manualCheckpoints = existingCheckpoints.filter(
      (cp) => !autoGeneratedNames.includes(cp.description)
    );

    // Get existing scheduled checkpoints from grid, filtered by target schedule type
    // twice-daily = only Morning + Evening; three-times = Morning + Mid-Day + Evening
    const scheduledNamesForType =
      scheduleType === 'twice-daily'
        ? ['Morning Alert', 'Evening Alert']
        : ['Morning Alert', 'Mid-Day Alert', 'Evening Alert'];
    const existingScheduledCheckpoints = existingCheckpoints.filter((cp) =>
      scheduledNamesForType.includes(cp.description)
    );

    // Start with manual checkpoints and existing scheduled checkpoints (schedule-type-specific)
    const checkpoints = [...manualCheckpoints, ...existingScheduledCheckpoints];

    // Create a set of times that already have scheduled checkpoints (to avoid duplicates)
    const existingScheduledTimes = new Set<string>();
    existingScheduledCheckpoints.forEach((cp) => {
      if (cp.expectedTime) {
        const timeKey = new Date(cp.expectedTime).toISOString();
        existingScheduledTimes.add(timeKey);
      }
    });
    const startDate = new Date(trip.startDate);
    const endDate = new Date(trip.endDate);

    // Set to start of day for iteration
    const startDay = new Date(startDate);
    startDay.setHours(0, 0, 0, 0);
    const endDay = new Date(endDate);
    endDay.setHours(23, 59, 59, 999);

    // Generate dates between start and end
    let currentDate = new Date(startDay);

    // Helper function to check if a scheduled checkpoint time already exists or was deleted
    const scheduledTimeExists = (expectedTime: Date): boolean => {
      const timeKey = expectedTime.toISOString();
      return existingScheduledTimes.has(timeKey);
    };

    // Helper to create a time in user's profile timezone and convert to UTC ISO
    const createTimeInUserTimezone = (
      date: Date,
      hour: number,
      minute: number = 0
    ): Date => {
      const tz =
        this.dateTimeService.timeZone$.value ||
        this.dateTimeService.browserDefaults.timeZone;
      // Format the date in user's timezone to get the correct calendar date
      const year = Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        timeZone: tz,
      }).format(date);
      const month = Intl.DateTimeFormat('en-US', {
        month: '2-digit',
        timeZone: tz,
      }).format(date);
      const day = Intl.DateTimeFormat('en-US', {
        day: '2-digit',
        timeZone: tz,
      }).format(date);
      const dateStr = `${year}-${month}-${day}`;
      const timeStr = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:00`;
      const parsed = dayjs.tz(
        `${dateStr} ${timeStr}`,
        'YYYY-MM-DD HH:mm:ss',
        tz
      );
      return parsed.isValid() ? parsed.toDate() : new Date(date);
    };

    while (currentDate <= endDay) {
      if (scheduleType === 'twice-daily') {
        // Morning Alert at 9:00 AM in user's timezone
        const morningTime = createTimeInUserTimezone(currentDate, 9, 0);
        // Only add if within trip window and doesn't already exist (preserves existing, doesn't regenerate deleted)
        if (
          morningTime >= startDate &&
          morningTime <= endDate &&
          !scheduledTimeExists(morningTime)
        ) {
          checkpoints.push({
            id: undefined,
            tripId: trip.id,
            description: 'Morning Alert',
            expectedTime: morningTime.toISOString(),
            status: 'Upcoming',
            notes: '',
            okayId: null,
          } as any);
        }

        // Evening Alert at 9:00 PM in user's timezone
        const eveningTime = createTimeInUserTimezone(currentDate, 21, 0);
        // Only add if within trip window and doesn't already exist (preserves existing, doesn't regenerate deleted)
        if (
          eveningTime >= startDate &&
          eveningTime <= endDate &&
          !scheduledTimeExists(eveningTime)
        ) {
          checkpoints.push({
            id: undefined,
            tripId: trip.id,
            description: 'Evening Alert',
            expectedTime: eveningTime.toISOString(),
            status: 'Upcoming',
            notes: '',
            okayId: null,
          } as any);
        }
      } else if (scheduleType === 'three-times') {
        // Morning Alert at 9:00 AM in user's timezone
        const morningTime = createTimeInUserTimezone(currentDate, 9, 0);
        // Only add if within trip window and doesn't already exist (preserves existing, doesn't regenerate deleted)
        if (
          morningTime >= startDate &&
          morningTime <= endDate &&
          !scheduledTimeExists(morningTime)
        ) {
          checkpoints.push({
            id: undefined, // Will be generated by database
            tripId: trip.id,
            description: 'Morning Alert',
            expectedTime: morningTime.toISOString(),
            status: 'Upcoming',
            notes: '',
            okayId: null,
          } as any);
        }

        // Mid-Day Alert at 1:00 PM in user's timezone
        const midDayTime = createTimeInUserTimezone(currentDate, 13, 0);
        // Only add if within trip window and doesn't already exist (preserves existing, doesn't regenerate deleted)
        if (
          midDayTime >= startDate &&
          midDayTime <= endDate &&
          !scheduledTimeExists(midDayTime)
        ) {
          checkpoints.push({
            id: undefined, // Will be generated by database
            tripId: trip.id,
            description: 'Mid-Day Alert',
            expectedTime: midDayTime.toISOString(),
            status: 'Upcoming',
            notes: '',
            okayId: null,
          } as any);
        }

        // Evening Alert at 9:00 PM in user's timezone
        const eveningTime = createTimeInUserTimezone(currentDate, 21, 0);
        // Only add if within trip window and doesn't already exist (preserves existing, doesn't regenerate deleted)
        if (
          eveningTime >= startDate &&
          eveningTime <= endDate &&
          !scheduledTimeExists(eveningTime)
        ) {
          checkpoints.push({
            id: undefined, // Will be generated by database
            tripId: trip.id,
            description: 'Evening Alert',
            expectedTime: eveningTime.toISOString(),
            status: 'Upcoming',
            notes: '',
            okayId: null,
          } as any);
        }
      }

      // Move to next day
      currentDate.setDate(currentDate.getDate() + 1);
    }

    // Update the grid immediately with generated checkpoints (for better UX)
    const updateGrid = () => {
      if (this.checkpointsGrid && this.checkpointsGrid.gridApi) {
        try {
          this.checkpointsGrid.rowData = checkpoints;
          this.checkpointsGrid.gridApi.setGridOption('rowData', checkpoints);
          this.cdr.detectChanges();
        } catch (error) {
          console.error(
            'Error updating grid in generateScheduledCheckpoints:',
            error
          );
          // Grid might have been destroyed, try again
          setTimeout(() => updateGrid(), 100);
        }
      } else {
        // Grid not ready yet, try again
        setTimeout(() => updateGrid(), 100);
      }
    };
    updateGrid();

    // Sanitize checkpoints before saving (remove temporary IDs)
    const sanitizedCheckpoints = this.sanitizeCheckpointsForSave(checkpoints);

    // Save the generated checkpoints
    this.subscriptions.add(
      this.tripService
        .saveTrip({ id: trip.id, checkpoints: sanitizedCheckpoints })
        .pipe(
          tap(() => {
            this.toastr.success(
              `Successfully saved ${scheduleType === 'twice-daily' ? 'twice daily' : 'three times daily'} alerts`
            );
            // Call success callback before refresh
            if (onSuccess) onSuccess();
          }),
          catchError((err) => {
            this.toastr.error('Failed to generate scheduled alerts');
            console.error(err);
            throw err;
          })
        )
        .subscribe()
    );
  }

  getAllCheckpointsFromGrid() {
    if (!this.checkpointsGrid || !this.checkpointsGrid.gridApi) {
      // Fallback to trip data if grid isn't ready
      return this.trip?.checkpoints || [];
    }

    const checkpoints = [];
    this.checkpointsGrid.gridApi.forEachNode((node) => {
      if (!node.rowPinned && node.data) {
        const checkpoint = this.checkpointsGrid.convertDataToAttr(node.data);
        // Strip temporary IDs (starting with 'temp-') before saving
        // The backend will generate proper UUIDs for new checkpoints
        if (
          checkpoint.id &&
          typeof checkpoint.id === 'string' &&
          checkpoint.id.startsWith('temp-')
        ) {
          checkpoint.id = undefined;
        }
        checkpoints.push(checkpoint);
      }
    });
    return checkpoints;
  }

  /**
   * Detects the schedule type from saved checkpoints and sets the form control accordingly
   * Only runs on initial load, not when user manually changes the dropdown or trip refreshes
   */
  detectAndSetScheduleFromCheckpoints(trip: TripAttributes) {
    // Only run once on initial load, or if user hasn't manually changed the schedule
    if (this.hasInitializedSchedule) {
      return;
    }

    // Don't override if user has manually selected a schedule
    if (
      this.hasScheduleChanged ||
      (this.selectedSchedule !== 'none' &&
        this.selectedSchedule !== this.savedSchedule)
    ) {
      return;
    }

    if (!trip || !trip.checkpoints || trip.checkpoints.length === 0) {
      // No checkpoints: default to 'none' unless user just chose Custom (we may have cleared auto-generated checkpoints)
      if (this.selectedSchedule !== 'custom') {
        this.selectedSchedule = 'none';
        this.savedSchedule = 'none';
        this.hasScheduleChanged = false;
      }
      return;
    }

    const checkpoints = trip.checkpoints;
    const autoGeneratedNames = [
      'Morning Alert',
      'Mid-Day Alert',
      'Evening Alert',
    ];

    // Check for auto-generated checkpoints
    const hasMorning = checkpoints.some(
      (cp) => cp.description === 'Morning Alert'
    );
    const hasMidDay = checkpoints.some(
      (cp) => cp.description === 'Mid-Day Alert'
    );
    const hasEvening = checkpoints.some(
      (cp) => cp.description === 'Evening Alert'
    );

    // Determine schedule type
    let detectedSchedule: string = 'none';
    if (hasMorning && hasMidDay && hasEvening) {
      // Has all three types, it's three-times
      detectedSchedule = 'three-times';
    } else if (hasMorning && hasEvening && !hasMidDay) {
      // Has morning and evening but no mid-day, it's twice-daily
      detectedSchedule = 'twice-daily';
    } else if (checkpoints.length > 0) {
      // Has checkpoints but not the auto-generated pattern, it's custom
      detectedSchedule = 'custom';
    }

    // Set the schedule state
    this.selectedSchedule = detectedSchedule;
    this.savedSchedule = detectedSchedule;
    this.hasScheduleChanged = false;
    this.hasInitializedSchedule = true; // Mark as initialized so we don't run again

    // Update the form control to match
    const scheduleOption = this.alertScheduleOptions$.value.find(
      (opt) => opt.value === detectedSchedule
    );
    if (scheduleOption) {
      this.formGroup
        .get('alertSchedule')
        .setValue(scheduleOption, { emitEvent: false });
    }

    // Force change detection to update the view
    this.cdr.detectChanges();

    // If there are checkpoints, show them in the grid after a delay to ensure grid is ready
    if (detectedSchedule !== 'none') {
      setTimeout(() => {
        this.loadSavedCheckpointsIntoGrid();
      }, 600); // Same delay as in valueChanges subscription
    }
  }

  /**
   * Loads saved checkpoints into the grid (used when page loads with existing checkpoints)
   */
  loadSavedCheckpointsIntoGrid() {
    const trip = this.trip;
    if (!trip || !trip.checkpoints || trip.checkpoints.length === 0) {
      return;
    }

    // Update the grid's rowData to show the saved checkpoints
    const updateGrid = () => {
      if (this.checkpointsGrid && this.checkpointsGrid.gridApi) {
        try {
          // Set the saved checkpoints directly
          this.checkpointsGrid.rowData = [...trip.checkpoints];
          this.checkpointsGrid.gridApi.setGridOption('rowData', [
            ...trip.checkpoints,
          ]);
          this.cdr.detectChanges();
        } catch (error) {
          console.error('Error loading checkpoints into grid:', error);
          // Grid might not be fully initialized, try again
          setTimeout(() => updateGrid(), 100);
        }
      } else {
        // Grid not ready yet, try again
        setTimeout(() => updateGrid(), 100);
      }
    };

    updateGrid();
  }

  /**
   * Sanitizes checkpoints before sending to backend:
   * - Removes temporary IDs (sets to null, which backend filters out)
   * - Ensures no temporary IDs are sent to backend
   */
  sanitizeCheckpointsForSave(checkpoints: any[]): any[] {
    if (!checkpoints || !Array.isArray(checkpoints)) {
      return [];
    }

    const sanitized = checkpoints
      .map((cp) => {
        if (!cp) return null;
        // Create a deep copy to avoid mutating the original
        const copy = JSON.parse(JSON.stringify(cp));
        // If it has a temporary ID, set to null (which will be filtered out by backend's .filter((id) => id))
        if (
          copy.id &&
          typeof copy.id === 'string' &&
          copy.id.startsWith('temp-')
        ) {
          copy.id = null; // Use null instead of undefined for better JSON serialization
        }
        return copy;
      })
      .filter((cp) => {
        if (!cp) return false;
        // Final safety check: if somehow a temp ID slipped through, remove the entire checkpoint
        if (cp.id && typeof cp.id === 'string' && cp.id.startsWith('temp-')) {
          console.error(
            'CRITICAL: Found temporary ID in checkpoint after sanitization, removing checkpoint:',
            cp.id
          );
          return false;
        }
        return true;
      });

    // Log if we removed any temporary IDs for debugging
    const hadTempIds = checkpoints.some(
      (cp) => cp?.id && typeof cp.id === 'string' && cp.id.startsWith('temp-')
    );
    if (hadTempIds) {
      console.log(
        'Sanitized checkpoints: removed temporary IDs. Original count:',
        checkpoints.length,
        'Sanitized count:',
        sanitized.length
      );
    }

    return sanitized;
  }

  previewScheduledCheckpoints(scheduleType: 'twice-daily' | 'three-times') {
    const trip = this.trip;
    if (!trip || !trip.startDate || !trip.endDate) {
      return;
    }

    // Get existing checkpoints from current grid state first (respects deletions and current state)
    // This ensures custom checkpoints that haven't been saved yet are preserved
    let existingCheckpoints: CheckpointAttributes[] = [];

    if (this.checkpointsGrid && this.checkpointsGrid.gridApi) {
      // Get current checkpoints from grid (includes any unsaved changes/deletions)
      this.checkpointsGrid.gridApi.forEachNode((node) => {
        if (!node.rowPinned && node.data) {
          const checkpoint = this.checkpointsGrid.convertDataToAttr(node.data);
          existingCheckpoints.push(checkpoint);
        }
      });
    }

    // Don't merge trip data - only use what's in the grid
    // This ensures deleted checkpoints don't reappear when switching schedules
    // The grid state is the source of truth for what checkpoints should be shown

    const autoGeneratedNames = [
      'Morning Alert',
      'Mid-Day Alert',
      'Evening Alert',
    ];

    // Only keep checkpoints that are NOT auto-generated AND have custom descriptions
    const manualCheckpoints = existingCheckpoints.filter((cp) => {
      // Keep only if it's not an auto-generated name AND has a non-empty description
      return (
        !autoGeneratedNames.includes(cp.description) &&
        cp.description &&
        cp.description.trim().length > 0
      );
    });

    // Start with only truly manual checkpoints
    const checkpoints = [...manualCheckpoints];
    const startDate = new Date(trip.startDate);
    const endDate = new Date(trip.endDate);

    // Set to start of day for iteration
    const startDay = new Date(startDate);
    startDay.setHours(0, 0, 0, 0);
    const endDay = new Date(endDate);
    endDay.setHours(23, 59, 59, 999);

    // Generate dates between start and end
    let currentDate = new Date(startDay);

    // Helper to create a time in user's profile timezone and convert to UTC ISO
    const createTimeInUserTimezone = (
      date: Date,
      hour: number,
      minute: number = 0
    ): Date => {
      const tz =
        this.dateTimeService.timeZone$.value ||
        this.dateTimeService.browserDefaults.timeZone;
      // Format the date in user's timezone to get the correct calendar date
      const year = Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        timeZone: tz,
      }).format(date);
      const month = Intl.DateTimeFormat('en-US', {
        month: '2-digit',
        timeZone: tz,
      }).format(date);
      const day = Intl.DateTimeFormat('en-US', {
        day: '2-digit',
        timeZone: tz,
      }).format(date);
      const dateStr = `${year}-${month}-${day}`;
      const timeStr = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:00`;
      const parsed = dayjs.tz(
        `${dateStr} ${timeStr}`,
        'YYYY-MM-DD HH:mm:ss',
        tz
      );
      return parsed.isValid() ? parsed.toDate() : new Date(date);
    };

    while (currentDate <= endDay) {
      if (scheduleType === 'twice-daily') {
        // Morning Alert at 9:00 AM in user's timezone
        const morningTime = createTimeInUserTimezone(currentDate, 9, 0);
        // Only add if within trip window
        if (morningTime >= startDate && morningTime <= endDate) {
          checkpoints.push({
            id: `temp-${uuidv4()}`, // Temporary unique ID for grid display
            tripId: trip.id,
            description: 'Morning Alert',
            expectedTime: morningTime.toISOString(),
            status: 'Upcoming',
            notes: '',
            okayId: null,
          } as any);
        }

        // Evening Alert at 9:00 PM in user's timezone
        const eveningTime = createTimeInUserTimezone(currentDate, 21, 0);
        // Only add if within trip window
        if (eveningTime >= startDate && eveningTime <= endDate) {
          checkpoints.push({
            id: `temp-${uuidv4()}`, // Temporary unique ID for grid display
            tripId: trip.id,
            description: 'Evening Alert',
            expectedTime: eveningTime.toISOString(),
            status: 'Upcoming',
            notes: '',
            okayId: null,
          } as any);
        }
      } else if (scheduleType === 'three-times') {
        // Morning Alert at 9:00 AM in user's timezone
        const morningTime = createTimeInUserTimezone(currentDate, 9, 0);
        // Only add if within trip window
        if (morningTime >= startDate && morningTime <= endDate) {
          checkpoints.push({
            id: `temp-${uuidv4()}`, // Temporary unique ID for grid display
            tripId: trip.id,
            description: 'Morning Alert',
            expectedTime: morningTime.toISOString(),
            status: 'Upcoming',
            notes: '',
            okayId: null,
          } as any);
        }

        // Mid-Day Alert at 1:00 PM in user's timezone
        const midDayTime = createTimeInUserTimezone(currentDate, 13, 0);
        // Only add if within trip window
        if (midDayTime >= startDate && midDayTime <= endDate) {
          checkpoints.push({
            id: `temp-${uuidv4()}`, // Temporary unique ID for grid display
            tripId: trip.id,
            description: 'Mid-Day Alert',
            expectedTime: midDayTime.toISOString(),
            status: 'Upcoming',
            notes: '',
            okayId: null,
          } as any);
        }

        // Evening Alert at 9:00 PM in user's timezone
        const eveningTime = createTimeInUserTimezone(currentDate, 21, 0);
        // Only add if within trip window
        if (eveningTime >= startDate && eveningTime <= endDate) {
          checkpoints.push({
            id: `temp-${uuidv4()}`, // Temporary unique ID for grid display
            tripId: trip.id,
            description: 'Evening Alert',
            expectedTime: eveningTime.toISOString(),
            status: 'Upcoming',
            notes: '',
            okayId: null,
          } as any);
        }
      }

      // Move to next day
      currentDate.setDate(currentDate.getDate() + 1);
    }

    // Update the grid's rowData to show the preview (without saving to DB)
    // Wait for grid to be ready
    const updateGrid = () => {
      if (this.checkpointsGrid && this.checkpointsGrid.gridApi) {
        try {
          // Set the new data directly - the checkpoints array already includes
          // all manual checkpoints merged from the current grid state
          this.checkpointsGrid.rowData = checkpoints;
          this.checkpointsGrid.gridApi.setGridOption('rowData', checkpoints);
          this.cdr.detectChanges();
        } catch (error) {
          console.error(
            'Error updating grid in previewScheduledCheckpoints:',
            error
          );
          // Grid might have been destroyed, try again
          setTimeout(() => updateGrid(), 100);
        }
      } else {
        // Grid not ready yet, try again
        setTimeout(() => updateGrid(), 100);
      }
    };

    updateGrid();
  }

  previewManualCheckpointsOnly() {
    const trip = this.trip;
    if (!trip) return;

    // Get checkpoints from current grid state only (respects deletions and current state)
    // Don't merge with trip data to avoid deleted checkpoints reappearing
    let existingCheckpoints: CheckpointAttributes[] = [];

    if (this.checkpointsGrid && this.checkpointsGrid.gridApi) {
      // Get current checkpoints from grid (includes any unsaved changes/deletions)
      this.checkpointsGrid.gridApi.forEachNode((node) => {
        if (!node.rowPinned && node.data) {
          existingCheckpoints.push(
            this.checkpointsGrid.convertDataToAttr(node.data)
          );
        }
      });
    }

    // Only fall back to trip data if grid is completely empty and not ready
    // This prevents deleted checkpoints from reappearing
    if (
      existingCheckpoints.length === 0 &&
      (!this.checkpointsGrid || !this.checkpointsGrid.gridApi)
    ) {
      existingCheckpoints = trip.checkpoints || [];
    }

    const autoGeneratedNames = [
      'Morning Alert',
      'Mid-Day Alert',
      'Evening Alert',
    ];
    const manualCheckpoints = existingCheckpoints.filter(
      (cp) => !autoGeneratedNames.includes(cp.description)
    );

    // Update the grid to show only manual checkpoints
    // Wait for grid to be ready
    const updateGrid = () => {
      if (this.checkpointsGrid && this.checkpointsGrid.gridApi) {
        try {
          // Clear the grid first to prevent any caching issues
          this.checkpointsGrid.rowData = [];
          this.checkpointsGrid.gridApi.setGridOption('rowData', []);

          // Then set the new data
          setTimeout(() => {
            if (this.checkpointsGrid && this.checkpointsGrid.gridApi) {
              this.checkpointsGrid.rowData = manualCheckpoints;
              this.checkpointsGrid.gridApi.setGridOption(
                'rowData',
                manualCheckpoints
              );
              this.cdr.detectChanges();
            }
          }, 0);
        } catch (error) {
          console.error(
            'Error updating grid in previewManualCheckpointsOnly:',
            error
          );
          // Grid might have been destroyed, try again
          setTimeout(() => updateGrid(), 100);
        }
      } else {
        // Grid not ready yet, try again
        setTimeout(() => updateGrid(), 100);
      }
    };

    updateGrid();
  }

  clearScheduledCheckpoints(
    onSuccess?: (savedCheckpoints: CheckpointAttributes[]) => void
  ) {
    const trip = this.trip;
    if (!trip) return;

    // Get current checkpoints from the grid (includes any manual edits)
    const existingCheckpoints = this.getAllCheckpointsFromGrid();
    const autoGeneratedNames = [
      'Morning Alert',
      'Mid-Day Alert',
      'Evening Alert',
    ];

    let checkpointsToSave: CheckpointAttributes[];

    if (this.selectedSchedule === 'none') {
      // For 'none', clear all checkpoints
      checkpointsToSave = [];
    } else {
      // For 'custom', keep only manual checkpoints (remove auto-generated ones)
      checkpointsToSave = existingCheckpoints.filter(
        (cp) => !autoGeneratedNames.includes(cp.description)
      );
    }

    // Sanitize checkpoints before saving (remove temporary IDs)
    const sanitizedCheckpoints =
      this.sanitizeCheckpointsForSave(checkpointsToSave);

    // Always save to ensure consistency
    this.subscriptions.add(
      this.tripService
        .saveTrip({ id: trip.id, checkpoints: sanitizedCheckpoints })
        .pipe(
          tap(() => {
            const message =
              this.selectedSchedule === 'custom'
                ? 'Switched to custom alerts'
                : this.selectedSchedule === 'none'
                  ? 'All alerts cleared'
                  : 'Scheduled alerts cleared';
            this.toastr.success(message);
            // Call success callback with the saved list so the grid can be updated
            if (onSuccess) onSuccess(checkpointsToSave);
            // Refresh trip so parent state stays in sync with server
            this.refresh$.next();
          }),
          catchError((err) => {
            this.toastr.error('Failed to clear scheduled alerts');
            console.error(err);
            throw err;
          })
        )
        .subscribe()
    );
  }

  clearAutoGeneratedCheckpoints(onSuccess?: () => void) {
    const trip = this.trip;
    if (!trip) {
      if (onSuccess) onSuccess();
      return;
    }

    // Remove ALL auto-generated checkpoints from the database
    const existingCheckpoints = trip.checkpoints || [];
    const autoGeneratedNames = [
      'Morning Alert',
      'Mid-Day Alert',
      'Evening Alert',
    ];
    const manualCheckpoints = existingCheckpoints.filter(
      (cp) => !autoGeneratedNames.includes(cp.description)
    );

    // Sanitize checkpoints before saving (remove temporary IDs)
    const sanitizedCheckpoints =
      this.sanitizeCheckpointsForSave(manualCheckpoints);

    // Only save if we actually have auto-generated checkpoints to remove
    if (manualCheckpoints.length < existingCheckpoints.length) {
      this.subscriptions.add(
        this.tripService
          .saveTrip({ id: trip.id, checkpoints: sanitizedCheckpoints })
          .pipe(
            tap(() => {
              // Silently clear without showing a toast - refresh trip data
              this.refresh$.next();
              // Wait longer for trip refresh to complete
              setTimeout(() => {
                if (onSuccess) onSuccess();
              }, 500);
            }),
            catchError((err) => {
              console.error('Failed to clear auto-generated checkpoints', err);
              if (onSuccess) onSuccess(); // Continue anyway
              throw err;
            })
          )
          .subscribe()
      );
    } else {
      // No checkpoints to remove
      if (onSuccess) onSuccess();
    }
  }

  ngOnDestroy() {
    this.destroyed = true;
    this.subscriptions.unsubscribe();
    this.tripFromTripServiceSub.unsubscribe();
    this.toastr.remove(this.toastrAllErrorToastId);
  }

  openCheckpointModal(rowNode?) {
    this.checkPointModal.openCheckpointModal(rowNode);
  }

  saveAlertSchedule() {
    if (!this.hasScheduleChanged) {
      // No schedule change, but save any manual edits to existing checkpoints
      if (this.checkpointsGrid) {
        this.checkpointsGrid.saveCheckpointData();
        this.toastr.success('Alert schedule saved');
      }
      return;
    }

    // Generate or clear checkpoints based on selected schedule
    if (
      this.selectedSchedule === 'twice-daily' ||
      this.selectedSchedule === 'three-times'
    ) {
      this.generateScheduledCheckpoints(this.selectedSchedule, () => {
        this.savedSchedule = this.selectedSchedule;
        this.hasScheduleChanged = false;
      });
    } else if (this.selectedSchedule === 'none') {
      // Clear all auto-generated checkpoints
      this.clearScheduledCheckpoints(() => {
        this.savedSchedule = this.selectedSchedule;
        this.hasScheduleChanged = false;
      });
    } else if (this.selectedSchedule === 'custom') {
      // Clear auto-generated checkpoints, keep manual ones, then save the grid state
      this.clearScheduledCheckpoints(() => {
        this.savedSchedule = this.selectedSchedule;
        this.hasScheduleChanged = false;
        // After clearing auto-generated, save any manual checkpoints in the grid
        if (this.checkpointsGrid && this.checkpointsGrid.gridRows.length > 0) {
          setTimeout(() => {
            this.checkpointsGrid.saveCheckpointData();
          }, 100);
        }
      });
    }
  }

  saveTrip() {
    // Closed trips are read-only. The backend rejects edits to a closed trip,
    // so don't fire the (auto-)save and surface a spurious error.
    if (this.trip?.tripStatus === 'Closed') {
      return;
    }
    this.subscriptions.add(
      this.tripService
        .saveTrip({
          id: this.tripId,
          endDate: this.formGroup.controls.endDate.value,
          startDate: this.formGroup.controls.startDate.value,
        })
        .pipe(
          tap(() => {
            //this.formGroup.reset();
            this.toastr.success('Trip has been saved', 'Success');
          }),
          catchError((err) => {
            this.toastr.error('Failed to Save Trip', 'Error');
            console.error(err);
            // Return Real Trip on Error
            return this.tripId;
          })
        )
        .subscribe()
    );
  }

  deleteTrip(ev) {
    try {
      // this trip pipe to accomplish the same thing
      this.tripService
        .deleteTrip(this.tripId)
        .pipe(
          take(1),
          tap((trip) => {
            this.router.navigate(['/search']);
            this.deleteTripConfirmModal.close('');
          })
        )
        .subscribe();
      this.toastr.success('Trip has been successfully deleted.', 'Success');
    } catch (err) {
      this.toastr.error(`There was an error deleting the trip.`, 'Error');
    }
  }

  /** Syntactic Sugar for `this.formGroup.controls[type].controls[index].controls[property]`*/
  getControl(type: string, index: number, property: string) {
    let control =
      this.formGroup.controls[type].controls[index].controls[property];
    if (!(control instanceof FormControl)) {
      throw new Error('Form Control not found');
    }
    return control;
  }
  debug(...args) {
    alert(...args);
  }

  // --------------------------------------Validators--------------------------------------------------------

  startDateValidator(control) {
    let invalid = control.value > this.endDateControl?.value;
    if (this.endDateControl?.invalid !== invalid) {
      setTimeout(() => this.endDateControl?.updateValueAndValidity(), 0);
    }
    if (invalid) {
      return {
        endDateBeforeStartDate: 'Start Date must be before End Date',
      };
    }
  }
  endDateValidator(control) {
    let invalid = control.value < this.startDateControl?.value;
    if (this.startDateControl?.invalid !== invalid) {
      setTimeout(() => this.startDateControl?.updateValueAndValidity(), 0);
    }
    if (invalid) {
      return {
        endDateBeforeStartDate: 'End Date must be after Start Date',
      };
    }
  }

  endDateInPastValidator(control) {
    let invalid = control?.value < new Date().toISOString();
    if (invalid) {
      return {
        endDateInPast: 'Please add a future return date',
      };
    }
  }

  // --------------------------------------AG GRID--------------------------------------------------------

  defaultColDef: ColDef = {
    sortable: false,
    suppressMovable: true,
    autoHeight: true,
    singleClickEdit: true,
    flex: 1,
    cellStyle: (params) => {
      return { textAlign: 'left', cursor: 'pointer' };
    },
  };

  public defaultRowSelection: RowSelectionOptions | 'single' | 'multiple' = {
    mode: 'multiRow',
    checkboxes: false,
    headerCheckbox: false,
    enableClickSelection: false,
  };

  /** Keep track of error state for all ag grid tables, and show or hide toast */
  updateErrorToast(
    whichGrid: keyof TripPageV3Component['gridErrorStates'],
    errorEmission: any
  ) {
    const MessageConsts = {
      message: 'Trip not saved. Fix all errors to save trip.',
      title: 'Trip has errors',
    };
    // Store the error states so we can keep track of them all
    this.gridErrorStates[whichGrid] = errorEmission;

    const tripIsClosed = this.trip.tripStatus === 'Closed';
    const noErrorsPresent = !this._hasGridErrors;
    // If closed, hide errors
    // If any errors, then show persistent error toast.
    // When all errors gone, then hide persistent error toast.
    if (tripIsClosed || noErrorsPresent) {
      this.toastr.remove(this.toastrAllErrorToastId);
    } else {
      // if any errors and trip is not closed
      let existingToast = this.toastr.findDuplicate(
        MessageConsts.title,
        MessageConsts.message,
        false,
        false
      );
      if (!existingToast) {
        this.toastrAllErrorToastId = this.toastr.error(
          'Trip not saved. Fix all errors to save trip.',
          'Trip has errors',
          { disableTimeOut: true }
        ).toastId;
      }
    }
  }

  private get _hasGridErrors() {
    return Object.values(this.gridErrorStates).some((x) => x);
  }
}
